import { MoreHorizontal } from 'lucide-react';
import { resolveLinearChipLabel, isImplChipUnchecked } from '../../../constants/chipStatus';
import { MiniCard } from '../mini-demo/MiniCard';
import { MiniCardHeader } from '../mini-demo/MiniCardHeader';
import { MiniCardTitle } from '../mini-demo/MiniCardTitle';
import { MiniCardMeta } from '../mini-demo/MiniCardMeta';
import { MiniIntegrationChip } from '../mini-demo/MiniIntegrationChip';
import { MiniCompletenessDonut } from '../mini-demo/MiniCompletenessDonut';
import type { Requirement } from './types';

interface RequirementCardProps {
  req: Requirement;
  selected: boolean;
  visible: boolean;
  dimmed: boolean;
  renderConnected?: boolean;
  implChipTarget?: string;
  deployChipTarget?: string;
}

const GITHUB_LABEL: Record<string, string> = {
  'Checking': 'Analyzing...',
  'Implemented': 'Implemented',
  'Partially Implemented': 'Partial',
  'Not Implemented': 'Not Implemented',
};

const RENDER_LABEL: Record<string, string> = {
  live: 'Live',
  not_deployed: 'Not Deployed',
  deploy_failed: 'Deploy Failed',
};

function resolveGitHubLabel(implStatus?: string): string {
  if (!implStatus || isImplChipUnchecked(implStatus)) return 'Unchecked';
  return GITHUB_LABEL[implStatus] ?? implStatus;
}

function resolveRenderLabel(deployStatus?: string): string | null {
  if (!deployStatus) return null;
  return RENDER_LABEL[deployStatus] ?? deployStatus;
}

export function RequirementCard({
  req,
  selected,
  visible,
  dimmed,
  renderConnected = true,
  implChipTarget,
  deployChipTarget,
}: RequirementCardProps) {
  const linearLabel = resolveLinearChipLabel(req.linearIssueIdentifier, req.status);
  const linearActive = !!req.linearIssueIdentifier && !!req.status;
  const githubLabel = resolveGitHubLabel(req.implStatus);
  const githubActive = req.implStatus === 'Implemented';
  const renderLabel = renderConnected ? resolveRenderLabel(req.deployStatus) : null;
  const renderActive = req.deployStatus === 'live';

  return (
    <MiniCard
      visible={visible}
      emphasis={selected}
      dimmed={dimmed}
      connectors={selected ? ['right'] : []}
    >
      <MiniCardHeader
        shortId={req.shortId}
        trailing={<MoreHorizontal size={8} className="text-text-quaternary" />}
      />
      <MiniCardTitle>{req.title}</MiniCardTitle>

      <div className="flex flex-wrap gap-1">
        <MiniIntegrationChip iconSrc="/linear.svg" label={linearLabel} tone={linearActive ? 'active' : 'default'} />
        <MiniIntegrationChip
          iconSrc="/github.svg"
          label={githubLabel}
          tone={githubActive ? 'active' : 'default'}
          target={implChipTarget}
        />
        {renderLabel && (
          <MiniIntegrationChip
            iconSrc="/render.svg"
            label={renderLabel}
            tone={renderActive ? 'active' : 'default'}
            target={deployChipTarget}
          />
        )}
      </div>

      <MiniCardMeta text={`${req.owner} - ${req.createdAt}`}>
        <MiniCompletenessDonut value={req.completeness} />
      </MiniCardMeta>
    </MiniCard>
  );
}
